import { Button, Conditional } from "@result/rui/components";
import { classNames } from "@result/rui/utils/component";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import CurrentAuthorization from "#/components/CurrentAuthorization";
import ConnectionState from "#/components/connection-state/ConnectionState";
import Breadcrumbs from "#/layout/Breadcrumbs";
import { MENU_STATE_COMPACT, MENU_STATE_EXPANDED } from "#/layout/LeftSidebar";
import { logout } from "#/utils/auth";

/**
 * Below the desktop breakpoint this navbar is the app ribbon, so it carries the
 * menu toggle too. `setMenuState` is absent when there is no sidebar to drive
 * (the standalone KIS header), in which case no toggle is rendered.
 *
 * @param {object} props
 * @param {string} [props.menuState] - MENU_STATE_COMPACT | MENU_STATE_EXPANDED
 * @param {Function} [props.setMenuState]
 */
export default function Header({ menuState, setMenuState }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const isMenuExpanded = menuState === MENU_STATE_EXPANDED;
  // On phones the account controls do not fit next to the page location, so
  // they collapse behind the person icon. See `_menu.scss`.
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountRef = useRef(null);

  const [userFullName] = useMemo(
    () => [
      auth?.user?.profile?.name,
      `${auth?.user?.profile?.preferred_username} (${auth?.user?.profile?.email})`
        .replace("()", "")
        .trim(),
    ],
    [auth]
  );

  const handleLogout = async () => {
    await logout({ auth });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountRef.current && !accountRef.current.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className="navbar has-shadow"
      role="navigation"
      aria-label="main navigation">
      <div className="navbar-brand">
        <Conditional condition={!!setMenuState}>
          <button
            type="button"
            className="navbar-menu-toggle"
            aria-label={t("mainNavigation.toggle")}
            onClick={() =>
              setMenuState(
                isMenuExpanded ? MENU_STATE_COMPACT : MENU_STATE_EXPANDED
              )
            }>
            <span
              className={classNames("icon is-size-4", {
                "icon-openmenu": !isMenuExpanded,
                "icon-closemenu": isMenuExpanded,
              })}
            />
          </button>
        </Conditional>
        <ConnectionState />
      </div>
      <div className="navbar-menu">
        <div className="navbar-start">
          <div className="navbar-item">
            <Breadcrumbs />
          </div>
        </div>
        <div className="navbar-end" ref={accountRef}>
          <button
            type="button"
            className="navbar-account-toggle"
            aria-expanded={isAccountOpen}
            aria-label={t("account.toggle")}
            onClick={() => setIsAccountOpen(!isAccountOpen)}>
            <span
              className={classNames("icon is-size-4", {
                "icon-user": !isAccountOpen,
                // not icon-closemenu — that is the burger's glyph, and the same
                // picture at both ends of the ribbon reads as the same control
                "icon-xmark": isAccountOpen,
              })}
            />
          </button>
          {/* `is-open` rather than Bulma's `is-active`, which styles navbar items. */}
          <div
            className={classNames(
              "navbar-item navbar-account is-size-6 is-gap-1 has-text-dark",
              { "is-open": isAccountOpen }
            )}>
            <span>{userFullName}</span>
            <CurrentAuthorization />
            <Button
              label={t("logout")}
              onClick={handleLogout}
              className="buttonLogout has-text-weight-normal"
            />
          </div>
        </div>
      </div>
    </nav>
  );
}
